import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _kebabCase from 'lodash/kebabCase';
import _uniq from 'lodash/uniq';
import _uniqBy from 'lodash/uniqBy';

import { JOB_LEVELS, PAID_JOBS_CATEGORY } from 'utils/constant';

import {
  getStateAbbr,
  getStateName,
  isStateAbbr,
  isStateName,
  isUserOutsideUS,
  mapLocationsForPaidJobs,
} from '../../utils';

export const WORK_FROM_HOME = 'Work From Home';
export const REMOTE_WORK_FROM_HOME = 'Remote (Work From Home)';
export const PART_TIME = 'Part Time';

export const REMOTE_CATEGORIES = {
  WORK_FROM_HOME,
  REMOTE_WORK_FROM_HOME,
  OPTION_FOR_REMOTE_WORK: 'Option for Remote Work',
  REMOTE_OR_FULLY_REMOTE: '100% remote or fully remote',
  REMOTE_DURING_PANDEMIC: 'Remote - During Pandemic',
  PARTIAL_REMOTE_WORK: 'Partial Remote Work',
};

export const INDUSTRIES = {
  ARCHITECTURE: 'Architecture and Engineering',
  ARTS: 'Arts, Entertainment, Sports, and Media',
  BUILDING: 'Building and Grounds Maintenance',
  BUSINESS: 'Business and Financial',
  COMMUNITY: 'Community and Social Services',
  COMPUTER: 'Computer and Mathematical',
  CONSTRUCTION: 'Construction and Extraction',
  EDUCATION: 'Education, Training, and Library',
  EXECUTIVE: 'Executive Management',
  FARMING: 'Farming, Fishing, and Forestry',
  FOOD: 'Food Preparation and Restaurant',
  HEALTHCARE: 'Healthcare Practitioner and Technical',
  HEALTHCARE_SUPPORT: 'Healthcare Support',
  INSTALLATION: 'Installation, Maintenance and Repair',
  LEGAL: 'Legal',
  LIFE: 'Life, Physical, and Social Science',
  MILITARY: 'Military',
  OFFICE: 'Office and Administrative',
  PERSONAL: 'Personal Care and Attendants',
  PRODUCTION: 'Production and Manufacturing',
  PROTECTIVE: 'Protective Service',
  SALES: 'Sales',
  TRANSPORTATION: 'Transportation',
};

export const COMPANY_CATEGORIES = {
  PROFESSIONAL: 'Professional',
  HOSPITALITY: 'Hospitality',
  INTERNET: 'Internet',
  MANUFACTURINGL: 'Manufacturing',
  RETAIL: 'Retail',
  HEALTHCARE: 'Health Care',
  FINANCE: 'Finance',
  TECHNOLOGY: 'Technology',
  CONSTRUCTION: 'Construction',
  REAL_STATE: 'Real Estate',
  AUTOMOTIVE: 'Automotive',
  TRANSPORTATION: 'Transportation',
  MEDIA: 'Media',
  ENERGY: 'Energy',
  PHARMA: 'Pharmaceutical',
  INSURANCE: 'Insurance',
  TELECOM: 'Telecommunication',
  UTILITIES: 'Utilities',
  GOVERMENT: 'Government',
  EDUCATION: 'Education',
  AGRICULTURE: 'Agriculture',
  NON_PROFIT: 'Non Profits',
  FORTUNE_500: 'Fortune 500',
  START_UP: 'Start-up',
};

export const filterSearchKey = 'filterSearchParams';

export const FILTER_TYPES = {
  AUTOSUGGEST: 'Autosuggest',
  SINGLE: 'Single',
  MULTI: 'Multi',
  MORE: 'More',
};

export const SHOW_AS = {
  BUTTON: 'button',
  LINK: 'link',
};

export const FILTER_PARAMS = {
  LOCATION: 'location',
  COMPANY: 'company',
  MAJOR: 'major',
  JOB_TITLE: 'jobTitle',
  DEPARTMENT: 'department',
  SALARY: 'salary',
  DISTANCE: 'distance',
  JOB_TYPE: 'jobType',
  JOB_LEVEL: 'jobLevel',
  EDUCATION: 'education',
  DATE_POSTED: 'datePosted',
  COLLEGE_TYPES: 'types',
  COLLEGE_DEGREES: 'degrees',
  COLLEGE_DEGREES_V2: 'degrees_v2',
  COLLEGE_SIZE: 'size',
  RACE_ETHNICITY: 'raceEthnicity',
  JOB_SITUATION: 'jobSituation',
  CARE_IN_JOB: 'careInJob',
  STRESSFUL_WORK: 'stressfulWork',
  REPETITIVE_WORK: 'repetitiveWork',
  JOB_PERKS: 'jobPerks',
  COMPANY_TYPE: 'companyType',
  COMPANY_BCP_TYPE: 'companyBCPType',
  COMPANY_SIZE: 'companySize',
  NOTIFICATION_FREQUENCY: 'notificationFrequency',
  STATUS: 'status',
  CUSTOM: 'custom',
  REMOTE: 'remote',
  COMPANY_INDUSTRY: 'companyIndustry',
};

export const CAROUSEL_FILTERS = {
  [FILTER_PARAMS.LOCATION]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.LOCATION,
    headerText: 'Location',
    buttonText: 'Location',
    options: {
      autoSuggest: true,
      remote: true,
    },
  },
  [FILTER_PARAMS.SALARY]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.SALARY,
    headerText: 'Minimum Salary Desired Per Year',
    buttonText: 'Salary',
    options: {
      salaryFieldLabel: 'Min',
    },
  },
  [FILTER_PARAMS.JOB_TITLE]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.JOB_TITLE,
    headerText: 'Job Title',
    buttonText: 'Job Title',
    options: {
      autoSuggest: true,
      defaultErrorMessage: 'Sorry we could not find that. Please try another job title.',
    },
  },
  [FILTER_PARAMS.COMPANY]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.COMPANY,
    headerText: 'Company',
    buttonText: 'Company',
    options: {
      autoSuggest: true,
    },
  },
  [FILTER_PARAMS.MAJOR]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.MAJOR,
    headerText: 'Major',
    buttonText: 'Major',
    options: {
      autoSuggest: true,
    },
  },
  [FILTER_PARAMS.DEPARTMENT]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.DEPARTMENT,
    headerText: 'Department',
    buttonText: 'Department',
    options: {
      autoSuggest: true,
    },
  },
  [FILTER_PARAMS.DISTANCE]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.DISTANCE,
    headerText: 'Distance',
    buttonText: 'Distance',
    valueRange: [
      { displayName: '0 miles', value: '0m' },
      { displayName: '10 miles', value: '10m' },
      { displayName: '25 miles', value: '25m' },
      { displayName: '50 miles', value: '50m' },
      { displayName: '75 miles', value: '75m' },
    ],
  },
  [FILTER_PARAMS.JOB_TYPE]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.JOB_TYPE,
    headerText: 'Job Type',
    buttonText: 'Job Type',
    valueRange: [
      { displayName: 'Full Time', value: 'Full Time' },
      { displayName: 'Part Time', value: 'Part Time' },
      { displayName: 'Internship', value: 'Internship' },
      { displayName: 'Temporary', value: 'Temporary' },
    ],
  },
  [FILTER_PARAMS.JOB_LEVEL]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.JOB_LEVEL,
    headerText: 'Job Level',
    buttonText: 'Job Level',
    valueRange: [
      { displayName: 'Entry Level', value: 'Entry Level' },
      { displayName: 'Junior Level', value: 'Junior Level' },
      { displayName: 'Mid Level', value: 'Mid Level' },
      { displayName: 'Senior Level', value: 'Senior Level' },
      { displayName: 'Management', value: 'Management' },
      { displayName: 'Executive', value: 'Executive' },
    ],
  },
  [FILTER_PARAMS.EDUCATION]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.EDUCATION,
    headerText: 'Your Highest Level of Education',
    buttonText: 'Education',
    options: {
      footerText: 'Include jobs with no education requirements.',
      educationSwitch: true,
    },
    valueRange: [
      { displayName: 'None', value: 'None' },
      { displayName: 'High School', value: 'High School Diploma' },
      { displayName: 'Associate', value: 'Associate' },
      { displayName: "Bachelor's", value: 'Bachelors' },
      { displayName: "Master's", value: 'Masters' },
      { displayName: 'Doctorate', value: 'Doctorate' },
    ],
  },
  [FILTER_PARAMS.DATE_POSTED]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.DATE_POSTED,
    headerText: 'Date Posted',
    buttonText: 'Date Posted',
    valueRange: [
      { displayName: 'Past Day', value: '1d' },
      { displayName: 'Past 3 Days', value: '3d' },
      { displayName: 'Past Week', value: '7d' },
      { displayName: 'Past Month', value: '30d' },
    ],
  },
  [FILTER_PARAMS.COLLEGE_TYPES]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.COLLEGE_TYPES,
    headerText: 'Type',
    buttonText: 'Type',
    valueRange: [
      { displayName: 'Public', value: 'Public' },
      { displayName: 'Private', value: 'Private' },
    ],
  },
  [FILTER_PARAMS.COLLEGE_DEGREES]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.COLLEGE_DEGREES,
    headerText: 'Highest Degree',
    buttonText: 'Highest Degree Level',
    valueRange: [
      { displayName: 'Graduate degree', value: 'Masters' },
      { displayName: "Bachelor's degree", value: 'Bachelors' },
      { displayName: 'Associate degree', value: 'Associate' },
    ],
  },
  [FILTER_PARAMS.JOB_SITUATION]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.JOB_SITUATION,
    headerText: 'Situation',
    buttonText: 'Situation',
    valueRange: [
      { displayName: 'Recently lost a job', value: 'recent-lost-job' },
      { displayName: 'Employed but looking', value: 'employed-casual' },
      {
        displayName: 'Finishing/Finished education',
        value: 'gaining-education',
      },
      { displayName: 'Re-entering workforce', value: 'reentering' },
      { displayName: 'Other', value: 'other' },
    ],
  },
  [FILTER_PARAMS.CARE_IN_JOB]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.CARE_IN_JOB,
    headerText: 'Care most about in a job',
    buttonText: 'Care most about in a job',
    valueRange: [
      { displayName: 'Getting the best pay', value: 'highest-paying' },
      {
        displayName: 'Working in a great environment',
        value: 'best-place-to-work',
      },
      { displayName: 'Job stability', value: 'stable' },
      {
        displayName: 'Starting as soon as possible',
        value: 'job-start-now',
      },
      {
        displayName: 'Growing and learning',
        value: 'opportunities-to-grow',
      },
    ],
  },
  [FILTER_PARAMS.STRESSFUL_WORK]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.STRESSFUL_WORK,
    headerText: 'Stressful work',
    buttonText: 'Stressful work',
    valueRange: [
      { displayName: 'Mind', value: 'mind' },
      { displayName: "Don't Mind", value: 'dont-mind' },
    ],
  },
  [FILTER_PARAMS.REPETITIVE_WORK]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.REPETITIVE_WORK,
    headerText: 'Repetitive work',
    buttonText: 'Repetitive work',
    valueRange: [
      { displayName: 'Mind', value: 'walk-around' },
      { displayName: "Don't Mind", value: 'mostly-sitting' },
    ],
  },
  [FILTER_PARAMS.JOB_PERKS]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.JOB_PERKS,
    headerText: 'Benefits or perks',
    buttonText: 'Benefits or perks',
    valueRange: [
      { displayName: 'Healthcare benefits', value: 'employee-benefits' },
      { displayName: 'Retirement plans', value: 'retirement-plan' },
      { displayName: 'Work-life balance', value: 'work-life-balance' },
      { displayName: 'Gym membership', value: 'gym' },
      { displayName: 'Free meals', value: 'free-meals' },
    ],
  },
  [FILTER_PARAMS.COMPANY_TYPE]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.COMPANY_TYPE,
    headerText: 'Company type',
    buttonText: 'Company type',
    valueRange: [
      {
        displayName: 'A small or medium company',
        value: 'small-company',
      },
      { displayName: 'A big company', value: 'big-company' },
      {
        displayName: 'A non-profit organization',
        value: 'non-profit-organization',
      },
      { displayName: 'Any/All', value: 'any-all' },
    ],
  },
  [FILTER_PARAMS.COMPANY_BCP_TYPE]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.COMPANY_BCP_TYPE,
    headerText: 'Company type',
    buttonText: 'Company type',
    valueRange: [
      { displayName: 'private', value: 'PRIVATE' },
      { displayName: 'public', value: 'PUBLIC' },
      { displayName: 'nonprofit', value: 'NONPROFIT' },
      { displayName: 'education', value: 'EDUCATION' },
      { displayName: 'personal', value: 'PERSONAL' },
      { displayName: 'government', value: 'GOVERNMENT' },
    ],
  },
  [FILTER_PARAMS.COMPANY_SIZE]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.COMPANY_SIZE,
    headerText: 'Company size',
    buttonText: 'Company size',
    valueRange: [
      { displayName: 'Small/Medium', value: 'SMALL_MEDIUM' },
      { displayName: 'Large', value: 'LARGE' },
    ],
  },
  [FILTER_PARAMS.NOTIFICATION_FREQUENCY]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.NOTIFICATION_FREQUENCY,
    headerText: 'Notification frequency',
    buttonText: 'Notification frequency',
    valueRange: [
      { value: 'daily', displayName: 'Daily' },
      { value: 'weekly', displayName: 'Weekly' },
      { value: 'monthly', displayName: 'Monthly' },
    ],
  },
  [FILTER_PARAMS.COLLEGE_DEGREES_V2]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.COLLEGE_DEGREES,
    headerText: 'Highest Degree',
    buttonText: 'Highest Degree Level',
    valueRange: [
      { displayName: 'Certificate degree', value: 'Certificate' },
      { displayName: 'Associate degree', value: 'Associate' },
      { displayName: "Bachelor's degree", value: 'Bachelors' },
      { displayName: 'Graduate degree', value: 'Masters' },
    ],
  },
  [FILTER_PARAMS.COLLEGE_SIZE]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.COLLEGE_SIZE,
    headerText: 'Size',
    buttonText: 'Size',
    valueRange: [
      { displayName: 'Small', value: 'Small' },
      { displayName: 'Medium', value: 'Medium' },
      { displayName: 'Large', value: 'Large' },
    ],
  },
  [FILTER_PARAMS.RACE_ETHNICITY]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.RACE_ETHNICITY,
    headerText: 'Race & Ethnicity',
    buttonText: 'Race & Ethnicity',
    valueRange: [
      { displayName: 'White', value: 'White' },
      { displayName: 'Hispanic or Latino', value: 'Hispanic or Latino' },
      { displayName: 'Asian', value: 'Asian' },
      { displayName: 'Black or African American', value: 'Black or African American' },
      { displayName: 'All Race and Ethnicity', value: 'All Race and Ethnicity' },
    ],
  },
  [FILTER_PARAMS.STATUS]: {
    type: FILTER_TYPES.SINGLE,
    param: FILTER_PARAMS.STATUS,
    headerText: 'Status',
    buttonText: 'Status',
    valueRange: [
      { displayName: 'Approved', value: 'APPROVED' },
      { displayName: 'Pending', value: 'PENDING' },
      { displayName: 'Rejected', value: 'REJECTED' },
      { displayName: 'Deferred', value: 'DEFERRED' },
    ],
  },
  [FILTER_PARAMS.REMOTE]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.REMOTE,
    headerText: 'Remote',
    buttonText: 'Remote',
    valueRange: [
      {
        displayName: REMOTE_CATEGORIES.REMOTE_DURING_PANDEMIC,
        value: REMOTE_CATEGORIES.REMOTE_DURING_PANDEMIC,
      },
      {
        displayName: REMOTE_CATEGORIES.PARTIAL_REMOTE_WORK,
        value: REMOTE_CATEGORIES.PARTIAL_REMOTE_WORK,
      },
      {
        displayName: REMOTE_CATEGORIES.OPTION_FOR_REMOTE_WORK,
        value: REMOTE_CATEGORIES.OPTION_FOR_REMOTE_WORK,
      },
      {
        displayName: REMOTE_CATEGORIES.REMOTE_OR_FULLY_REMOTE,
        value: REMOTE_CATEGORIES.REMOTE_OR_FULLY_REMOTE,
      },
    ],
  },
  [FILTER_PARAMS.COMPANY_INDUSTRY]: {
    type: FILTER_TYPES.MULTI,
    param: FILTER_PARAMS.COMPANY_INDUSTRY,
    headerText: 'Company Industry',
    buttonText: 'More',
    options: {
      autoSuggest: true,
      autoSuggestFocus: false,
    },
    alwaysShowButtonText: true,
    staticSuggestions: [
      ...Object.keys(INDUSTRIES).map((industry) => ({
        displayName: INDUSTRIES[industry],
        value: INDUSTRIES[industry],
      })),
      ...Object.keys(COMPANY_CATEGORIES).map((category) => ({
        displayName: COMPANY_CATEGORIES[category],
        value: COMPANY_CATEGORIES[category],
      })),
    ],
  },
};

const JOB_TITLE_AUTO_SUGGEST_FILTER = {
  ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
  type: FILTER_TYPES.AUTOSUGGEST,
  options:{
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE].options,
    openFilterType: FILTER_TYPES.AUTOSUGGEST,
  }
}

export const COMPANY_JOBS_FITERS = [
  JOB_TITLE_AUTO_SUGGEST_FILTER,
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY],
];

export const COMPANY_EXECUTIVES_FILTERS = [
  JOB_TITLE_AUTO_SUGGEST_FILTER,
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY],
];

export const COMPANY_DEMOGRAPHICS_FILTERS = [
  JOB_TITLE_AUTO_SUGGEST_FILTER,
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY],
];

export const COMPANY_COMPETITORS_FILTERS = [
  ...COMPANY_JOBS_FITERS,
];

export const MAJOR_JOBS_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const CMP_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const MATCHES_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
  CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY_INDUSTRY],
];

export const CMP_JOBS_FILTERS = [CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE], ...CMP_FILTERS];

export const CMP_REMOTE_JOBS_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const EDUCATION_COLLEGES_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.COLLEGE_TYPES],
  CAROUSEL_FILTERS[FILTER_PARAMS.COLLEGE_DEGREES],
];

export const CITY_PAGE_JOBS_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const JOB_SEARCH_REMOTE_JOBS_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
  CAROUSEL_FILTERS[FILTER_PARAMS.REMOTE],
];

export const JOB_SEARCH_REMOTE_COMPANIES_FILTERS = [
  {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
    type: FILTER_TYPES.MULTI,
  },
  CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const COMPANY_SITEMAP_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const PROFILE_FILTERS_PARAMS = {
  JOB_NOTIFICATIONS: 'jobNotifications',
  COMPANY_NOTIFICATIONS: 'companyNotifications',
};

export const PROFILE_FILTERS = {
  [PROFILE_FILTERS_PARAMS.JOB_NOTIFICATIONS]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.NOTIFICATION_FREQUENCY],
    param: PROFILE_FILTERS_PARAMS.JOB_NOTIFICATIONS,
  },
  [PROFILE_FILTERS_PARAMS.COMPANY_NOTIFICATIONS]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.NOTIFICATION_FREQUENCY],
    param: PROFILE_FILTERS_PARAMS.COMPANY_NOTIFICATIONS,
  },
};

export const PROFILE_JOBS_FILTERS = {
  [FILTER_PARAMS.MAJOR]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.MAJOR],
    type: FILTER_TYPES.SINGLE,
  },
  [FILTER_PARAMS.EDUCATION]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
    options: {
      educationSwitch: false,
    },
    valueRange: [
      { displayName: 'None', value: 'none' },
      { displayName: 'High School', value: 'highschool' },
      { displayName: 'Associate', value: 'associate' },
      { displayName: "Bachelor's", value: 'bachelors' },
      { displayName: "Master's", value: 'masters' },
      { displayName: 'Doctorate', value: 'doctorate' },
    ],
  },
  [FILTER_PARAMS.JOB_LEVEL]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
    valueRange: Object.entries(JOB_LEVELS).map(([displayName, value]) => ({ displayName, value })),
  },
  [FILTER_PARAMS.JOB_SITUATION]: CAROUSEL_FILTERS[FILTER_PARAMS.JOB_SITUATION],
  [FILTER_PARAMS.JOB_TITLE]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
    type: FILTER_TYPES.MULTI,
    options: {
      ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE].options,
      profileJobs: true,
    },
  },
  [FILTER_PARAMS.JOB_TYPE]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
    valueRange: CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE].valueRange.map((item) => ({
      ...item,
      value: _kebabCase(item.value),
    })),
  },
  [FILTER_PARAMS.SALARY]: CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  [FILTER_PARAMS.COMPANY]: CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY],
  [FILTER_PARAMS.LOCATION]: CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  [FILTER_PARAMS.DISTANCE]: CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  [FILTER_PARAMS.CARE_IN_JOB]: CAROUSEL_FILTERS[FILTER_PARAMS.CARE_IN_JOB],
  [FILTER_PARAMS.STRESSFUL_WORK]: CAROUSEL_FILTERS[FILTER_PARAMS.STRESSFUL_WORK],
  [FILTER_PARAMS.REPETITIVE_WORK]: CAROUSEL_FILTERS[FILTER_PARAMS.REPETITIVE_WORK],
  [FILTER_PARAMS.JOB_PERKS]: CAROUSEL_FILTERS[FILTER_PARAMS.JOB_PERKS],
  [FILTER_PARAMS.COMPANY_TYPE]: CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY_TYPE],
  [FILTER_PARAMS.COMPANY_INDUSTRY]: CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY_INDUSTRY],
};

export const PROFILE_SALARY_FILTERS = {
  [FILTER_PARAMS.JOB_TITLE]: CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
  [FILTER_PARAMS.LOCATION]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
    type: FILTER_TYPES.SINGLE,
  },
  [FILTER_PARAMS.JOB_LEVEL]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
    type: FILTER_TYPES.SINGLE,
  },
  [FILTER_PARAMS.SALARY]: CAROUSEL_FILTERS[FILTER_PARAMS.SALARY],
  [FILTER_PARAMS.JOB_TYPE]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
    valueRange: [
      ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE].valueRange,
      { displayName: 'Remote', value: 'remote' },
    ],
  },
  [FILTER_PARAMS.EDUCATION]: {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
    options: {
      educationSwitch: false,
    },
  },
};

export const JOB_TITLE_REVIEW_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const MAJOR_BEST_COLLEGES_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.COLLEGE_TYPES],
  CAROUSEL_FILTERS[FILTER_PARAMS.COLLEGE_DEGREES_V2],
  CAROUSEL_FILTERS[FILTER_PARAMS.COLLEGE_SIZE],
];

export const FOREIGN_WORKERS_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.LOCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DISTANCE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TYPE],
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_LEVEL],
  CAROUSEL_FILTERS[FILTER_PARAMS.EDUCATION],
  CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED],
];

export const COMPANY_REVIEW_FILTERS = [...FOREIGN_WORKERS_FILTERS];

export const COMPANY_REVIEW_FILTERS_SIMILAR_COMPANIES = [
  CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY],
  ...FOREIGN_WORKERS_FILTERS,
];

export const DLP_SIMILAR_FILTERS = [
  CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
  ...FOREIGN_WORKERS_FILTERS,
];

export const DETAILED_ANSWER_TITLE = [
  {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
    type: FILTER_TYPES.AUTOSUGGEST,
    options: {
      autoSuggestPlaceholder: 'Enter job title',
    },
  },
  ...CMP_JOBS_FILTERS.slice(1),
];

export const DETAILED_ANSWER_COMPANY = [
  {
    ...CAROUSEL_FILTERS[FILTER_PARAMS.COMPANY],
    type: FILTER_TYPES.AUTOSUGGEST,
    options: {
      autoSuggestPlaceholder: 'Enter company',
    },
  },
  ...COMPANY_JOBS_FITERS,
];

export const SITEMAP_TOP_LEVEL = [...CITY_PAGE_JOBS_FILTERS];

const setupOptions = (filterOptions = {}, newFieldOptions = {}) => ({
  ...filterOptions,
  ...newFieldOptions,
});

export const setupFilter = (filter = {}, newField = {}) =>
  Object.assign({}, filter, {
    ...newField,
    options: setupOptions(filter.options, newField.options),
  });

export const setupFilters = (filters = [], newFields) => {
  const newFilters = [];
  filters.forEach((filter, index) => {
    newFilters.push(setupFilter(filter, newFields[index]));
  });
  return newFilters;
};

export const mapAutoSuggestValue = (param, valueObj) => {
  let value = {};
  switch (param) {
    case FILTER_PARAMS.JOB_TITLE:
      value.originalSearch = valueObj.original_name;
      value.normalizedTitle = valueObj.name;
      break;
    default:
      value = valueObj;
  }
  return value;
};

export const prefillRemoteLocation = (locations, user) => {
  const hasRemote = user?.extendedLocations?.find((item) => item === WORK_FROM_HOME);

  if (hasRemote) {
    locations.push({
      displayName: WORK_FROM_HOME,
      value: WORK_FROM_HOME,
    });
  }
}

export const validatePaidJobsPayload = (paidAPIPayload, payload = {}) => {
  switch (paidAPIPayload.category) {
    case PAID_JOBS_CATEGORY.LOCATION:
      return !!payload.locations?.length;
    case PAID_JOBS_CATEGORY.CAREER_CLUSTER:
      return !!(
        payload.locations?.length ||
        payload.remoteCategories?.length ||
        paidAPIPayload.remoteCategories?.length
      );
    case PAID_JOBS_CATEGORY.COMPANY_CATEGORIES:
    case PAID_JOBS_CATEGORY.COMPANY_CLUSTER:
      return !!paidAPIPayload.primaryCompanyID;
    default:
      return !!(paidAPIPayload.locations?.length || payload.locations?.length);
  }
};

export const checkPayloadForCompanyJobs = (payload = {}, paidAPIPayload = {}, filters = {}) => {
  const newPayload = { ...payload };
  if (
    paidAPIPayload.primaryCompanyID &&
    payload.remoteCategories?.length &&
    filters.remoteCategories?.length &&
    !filters.locations?.length
  ) {
    delete newPayload.locations;
  }
  return newPayload;
};

export const mapPaidJobFilters = (filters = {}, paidAPIPayload = {}) => {
  const newFilters = { ...paidAPIPayload };
  const paidJobCategory = paidAPIPayload.category || filters.category;
  Object.keys(filters).forEach((key) => {
    switch (key) {
      // Not to include filters/keys in the paid jobs API
      case 'jobTitle':
      case 'title':
        if (paidJobCategory === PAID_JOBS_CATEGORY.CAREER_CLUSTER) {
          newFilters.titles = [{ normalizedTitle: filters[key], originalSearch: filters[key] }];
        } else if (
          [PAID_JOBS_CATEGORY.COMPANY_CATEGORIES, PAID_JOBS_CATEGORY.COMPANY_CLUSTER].includes(
            paidJobCategory
          )
        ) {
          newFilters.title = filters[key];
        } else {
          newFilters[key] = filters[key];
        }
        break;
      case 'titles':
        break;
      case 'degrees':
        if (!Array.isArray(filters[key])) {
          newFilters[key] = [filters[key]];
        } else {
          newFilters[key] = filters[key];
        }
        break;
      case 'salary': {
        const isSalaryRange = Array.isArray(filters[key]);
        newFilters[isSalaryRange ? 'salaryRange' : key] = isSalaryRange
          ? filters[key]
          : parseInt(filters[key]) || 0;
        break;
      }
      case 'locations':
        newFilters[key] = mapLocationsForPaidJobs(filters.locations);
        break;
      default:
        newFilters[key] = filters[key];
    }
  });
  if (
    paidJobCategory === PAID_JOBS_CATEGORY.CAREER_CLUSTER &&
    !filters.locations &&
    paidAPIPayload.locations
  ) {
    delete newFilters.locations;
  }
  return newFilters;
};

export const mapFilterValues = (unmappedFiltersValue = {}) => {
  let mappedValues = {};
  const unmappedFiltersValueCopy = { ...unmappedFiltersValue };

  Object.keys(unmappedFiltersValueCopy).forEach((param) => {
    let key = '';
    switch (param) {
      case FILTER_PARAMS.JOB_TITLE:
        if (unmappedFiltersValueCopy[param].length > 1) {
          key = 'titles';
        } else {
          key = 'title';
        }
        break;
      case FILTER_PARAMS.LOCATION:
        key = 'locations';
        break;
      case FILTER_PARAMS.DISTANCE:
        key = 'radiusCategory';
        break;
      case FILTER_PARAMS.JOB_LEVEL:
        key = 'jobLevels';
        break;
      case FILTER_PARAMS.DATE_POSTED:
        key = 'postingDateRange';
        break;
      case FILTER_PARAMS.COMPANY:
        key = 'companyID';
        break;
      case FILTER_PARAMS.EDUCATION:
        key = 'degrees';
        break;
      case FILTER_PARAMS.REMOTE:
        key = 'remoteCategories';
        break;
      case FILTER_PARAMS.SALARY:
        if (Array.isArray(unmappedFiltersValueCopy[param]?.[0]?.value)) {
          key = 'salaryRange';
        } else {
          key = 'salary';
        }
        break;
      default:
        key = param;
        break;
    }
    if (param === FILTER_PARAMS.LOCATION) {
      const index = unmappedFiltersValueCopy[param].findIndex(
        ({ displayName }) => displayName === REMOTE_WORK_FROM_HOME || displayName === WORK_FROM_HOME
      );

      if (index !== -1) {
        mappedValues.remoteCategories = [WORK_FROM_HOME];
        unmappedFiltersValueCopy[param] = unmappedFiltersValueCopy[param].filter(
          ({ displayName }) =>
            displayName !== WORK_FROM_HOME && displayName !== REMOTE_WORK_FROM_HOME
        );
      }
    }
    if (param === FILTER_PARAMS.COMPANY_INDUSTRY && unmappedFiltersValueCopy[param]?.length === 0) {
      mappedValues.industries = [];
      mappedValues.companyCategories = [];
    }
    if (unmappedFiltersValueCopy[param].length > 1) {
      if (param === FILTER_PARAMS.COMPANY_INDUSTRY) {
        mappedValues.industries = unmappedFiltersValueCopy[param]
          .filter(({ value }) => Object.values(INDUSTRIES).includes(value))
          .map(({ value }) => value);
        mappedValues.companyCategories = unmappedFiltersValueCopy[param]
          .filter(({ value }) => Object.values(COMPANY_CATEGORIES).includes(value))
          .map(({ value }) => value);
      } else if (param === FILTER_PARAMS.COMPANY) {
          mappedValues[key] = unmappedFiltersValueCopy[param].map(({ value }) => value?.companyID);
      } else {
        mappedValues[key] = unmappedFiltersValueCopy[param].map(({ value }) => value);
      }
    } else if (unmappedFiltersValueCopy[param].length === 1) {
      switch (param) {
        case FILTER_PARAMS.COMPANY:
          mappedValues[key] = unmappedFiltersValueCopy[param].map(({ value }) => value?.companyID || value);
          break;
        case FILTER_PARAMS.JOB_TYPE:
        case FILTER_PARAMS.JOB_LEVEL:
        case FILTER_PARAMS.LOCATION:
        case FILTER_PARAMS.REMOTE:
          mappedValues[key] = unmappedFiltersValueCopy[param].map(({ value }) => value);
          break;
        case FILTER_PARAMS.JOB_TITLE:
          if (_get(unmappedFiltersValueCopy[param], '[0].value.socCode', '')) {
            mappedValues = {
              ...mappedValues,
              ..._get(unmappedFiltersValueCopy[param], '[0].value', ''),
            };
          } else {
            mappedValues[key] = _get(
              unmappedFiltersValueCopy[param],
              '[0].value.normalizedTitle',
              _get(unmappedFiltersValueCopy[param], '[0].value', '')
            );
          }
          break;
        case FILTER_PARAMS.COMPANY_INDUSTRY:
          mappedValues.industries = unmappedFiltersValueCopy[param]
            .filter(({ value }) => Object.values(INDUSTRIES).includes(value))
            .map(({ value }) => value);
          mappedValues.companyCategories = unmappedFiltersValueCopy[param]
            .filter(({ value }) => Object.values(COMPANY_CATEGORIES).includes(value))
            .map(({ value }) => value);
          break;
        case FILTER_PARAMS.EDUCATION: {
          const val = _get(unmappedFiltersValueCopy[param], '[0].value', '');
          if (Array.isArray(val) && val.findIndex((x) => x === 'None') >= 0) {
            mappedValues[key] = _uniq(['None', ...(Array.isArray(val) ? val : [val])]);
          } else {
            mappedValues[key] = _uniq(Array.isArray(val) ? val : [val]);
          }
          break;
        }
        default:
          mappedValues[key] = _get(unmappedFiltersValueCopy[param], '[0].value', '');
          break;
      }
    }
  });

  return mappedValues;
};

export const addRemoteFilterToPaidJobs = (filterQueue = [], criteria = {}) => {
  if (isUserOutsideUS(criteria) || !filterQueue.length) return {};

  const currentFilters = mapFilterValues(filterQueue[filterQueue.length - 1]);

  if (!currentFilters?.remoteCategories) return {};

  return { remoteCategories: [WORK_FROM_HOME] };
};

export const getFiltersFromHashedUrl = (hashedUrl = '') => {
  const url = decodeURIComponent(hashedUrl);
  let filters = {};
  if (url.indexOf('#') !== -1) {
    let substrings = url.split('#');
    if (substrings.length === 2) {
      substrings = substrings[1].split('&');
    }
    substrings.forEach((text) => {
      let [key, oldValue] = text.split('=');
      if (key && oldValue) {
        let value = oldValue;
        switch (key) {
          case 'stateName':
          case 'stateCode':
          case 'state':
          case FILTER_PARAMS.LOCATION:
            if (key === 'location' && value.includes(', ')) {
              const [city, state] = value.split(', ');
              if (city || state) {
                value = { city: city || '', state: state || '' };
              }
            } else if (
              key === 'stateName' ||
              key === 'state' ||
              key === 'stateCode' ||
              (key === 'location' && (isStateAbbr(value) || isStateName(value)))
            ) {
              let city = substrings.find((str) => str.includes('city='));
              if (city) {
                city = city.split('=')?.[1];
              }
              value = { city: city || '', state: isStateAbbr(value) ? value : getStateAbbr(value) };
            }
            key = 'location';
            break;
          case 'remote':
            value = { remoteCategories: true };
            key = 'location';
            break;
          case 'radiusCategory':
          case FILTER_PARAMS.DISTANCE:
            key = FILTER_PARAMS.DISTANCE;
            break;
          case 'jobType':
          case FILTER_PARAMS.JOB_TYPE:
            key = FILTER_PARAMS.JOB_TYPE;
            break;
          case 'jobLevels':
          case FILTER_PARAMS.JOB_LEVEL:
            key = FILTER_PARAMS.JOB_LEVEL;
            break;
          case 'degrees':
          case FILTER_PARAMS.EDUCATION:
            key = FILTER_PARAMS.EDUCATION;
            break;
          case 'postingDateRange':
          case FILTER_PARAMS.DATE_POSTED:
            key = FILTER_PARAMS.DATE_POSTED;
            break;
        }
        if (filters[key] && filters[key].length) {
          filters = {
            ...filters,
            [key]: [...filters[key], value],
          };
        } else {
          filters = {
            ...filters,
            [key]: [value],
          };
        }
      }
    });
  }
  return filters;
};

export const mapFilterSearchParams = (params = {}) => {
  const newParams = {};
  Object.keys(params).forEach((param) => {
    let values = params[param];
    switch (param) {
      case FILTER_PARAMS.LOCATION:
        values = values.map((value) => {
          if (value.remoteCategories) {
            return {
              displayName: WORK_FROM_HOME,
              value: WORK_FROM_HOME,
            };
          }
          if (value.value && value.displayName) {
            return value;
          }
          return {
            displayName: value.city
              ? `${value.city}, ${value.state}`
              : isStateAbbr(value.state)
              ? getStateName(value.state)
              : value.state,
            value,
          };
        });
        break;
      case FILTER_PARAMS.DISTANCE:
      case FILTER_PARAMS.JOB_TYPE:
      case FILTER_PARAMS.JOB_LEVEL:
      case FILTER_PARAMS.DATE_POSTED:
        values = values?.map((x) => {
          return x?.value || x;
        });
        values = CAROUSEL_FILTERS[param].valueRange.filter(
          (obj) => values.includes(obj.value) || values.includes(obj.displayName)
        );
        break;
      case FILTER_PARAMS.EDUCATION:
        values = values[0]?.value || [];
        values = CAROUSEL_FILTERS[param].valueRange.filter(
          (obj) => values.includes(obj.value) || values.includes(obj.displayName)
        );
        break;
      case FILTER_PARAMS.JOB_TITLE:
        values = values.map((jobTitle) =>
          jobTitle.displayName && jobTitle.value
            ? jobTitle
            : {
                displayName: jobTitle,
                value: {
                  originalSearch: jobTitle,
                  normalizedTitle: jobTitle,
                },
              }
        );
        break;
      default:
        break;
    }
    newParams[param] = values;
  });
  return newParams;
};

export const getInitialFilters = (filters, criteria, userLocation = [], queryFilters = {}) => {
  if ((criteria && criteria.title) || (criteria && criteria.major) || !_isEmpty(queryFilters)) {
    return filters.map((filter) => {
      switch (filter.param) {
        case FILTER_PARAMS.JOB_TITLE:
          if (criteria && criteria.title) {
            return {
              ...filter,
              preselectedItems: [
                {
                  displayName: criteria.title,
                  value: {
                    normalizedTitle: criteria.title,
                    originalSearch: criteria.title,
                  },
                },
              ],
            };
          }
          return filter;
        case FILTER_PARAMS.SALARY:
          if (criteria && criteria.title) {
            return {
              ...filter,
              options: {
                ...filter.options,
                jobTitle: criteria.title,
              },
            };
          }
          return filter;
        case FILTER_PARAMS.LOCATION:
          if (
            !(criteria && criteria.companyId) &&
            userLocation &&
            userLocation.length &&
            !filter.preselectedItems
          ) {
            return {
              ...filter,
              preselectedItems: userLocation.map(({ city, state }) => ({
                displayName: city ? `${city}, ${state}` : getStateName(state),
                value: { city, state },
              })),
              showAsworkFromHome: true,
            };
          }
          return {
            ...filter,
            showAsworkFromHome: true,
          };
        case FILTER_PARAMS.DATE_POSTED:
          if (queryFilters && queryFilters.dateposted) {
            let filterValue = {};
            switch (queryFilters.dateposted) {
              case 'past-day':
                filterValue = CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED].valueRange[0];
                break;
              case 'past-3days':
                filterValue = CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED].valueRange[1];
                break;
              case 'past-week':
                filterValue = CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED].valueRange[2];
                break;
              case 'past-month':
                filterValue = CAROUSEL_FILTERS[FILTER_PARAMS.DATE_POSTED].valueRange[3];
            }
            return {
              ...filter,
              preselectedItems: _uniqBy(
                [...(filter.preselectedItems || []), filterValue],
                'displayName'
              ),
            };
          }
          return filter;
        default:
          return filter;
      }
    });
  }
  return filters.map((filter) => {
    switch (filter.param) {
      case FILTER_PARAMS.LOCATION:
        return {
          ...filter,
          showAsworkFromHome: true,
        };
      default:
        return filter;
    }
  });
};

export const isQueryParams = (query = {}) => {
  if (!_isEmpty(query)) {
    // Now just dateposted filter. In future there may be more filters.
    return !!query.dateposted;
  }
  return false;
};

/**
 * Enable filter item
 *
 * @param {Object} obj
 */
export const enable = (obj) => ({ ...obj, enabled: true });

/**
 * Prefill filter with data
 *
 * @param {Array} filter.valueRange
 * @param {Array} data
 */
export const prefill = (valueRange, data) => {
  const items = [];

  if (data) {
    const values = Array.isArray(data) ? data : [data];

    values.forEach((value) => {
      const item = valueRange.find((item) => item.value === value);

      if (item) {
        items.push(enable(item));
      }
    });
  }

  return items;
};
